import React, { useContext, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  usersAPI,
  authAPI,
  trainingGroupsAPI,
  crewsAPI,
  trainingSessionsAPI,
  autoplannerAPI,
  notificationsAPI,
} from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { AuthContext } from '../contexts/AuthContext';
import { isAdmin, getCoachingTrainingGroups } from '../utils/roles';
import { useNotifications } from '../components/notifications';
import { CreateGroupModal } from '../components/training_groups';
import { CreateCrewModal, EditCrewModal } from '../components/crews';
import { parseRiggingConfig } from '../utils/riggingValidation';
import { extractErrorMessage } from '../utils/errorHandling';

// Import new admin components
import StatisticsCards from '../components/admin/StatisticsCards';
import BulkActions from '../components/admin/BulkActions';
import UsersTable from '../components/admin/UsersTable';
import SystemAdministrators from '../components/admin/SystemAdministrators';
import TrainingGroups from '../components/admin/TrainingGroups';
import Crews from '../components/admin/Crews';
import AdminNotifications from '../components/admin/AdminNotifications';
import PendingRequests from '../components/admin/PendingRequests';
import EmailChangeModal from '../components/admin/EmailChangeModal';
import ConfirmationModal from '../components/admin/ConfirmationModal';
import UserDetailsModal from '../components/admin/UserDetailsModal';
import AdminNavigation from '../components/admin/AdminNavigation';

const Admin = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotifications();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [detailsUserId, setDetailsUserId] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    group_type: 'intermediate',
    is_active: true,
    availability_reminder_enabled: false,
    availability_reminder_day: 6,
  });
  const [isCreateCrewOpen, setIsCreateCrewOpen] = useState(false);
  const [isEditCrewOpen, setIsEditCrewOpen] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [newCrew, setNewCrew] = useState({ name: '', boat_type: '4x', is_experimental: false });

  // Bulk actions state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedBulkDeleteUsers, setSelectedBulkDeleteUsers] = useState(new Set());
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [selectedCrews, setSelectedCrews] = useState(new Set());
  const [bulkActionType, setBulkActionType] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Notification state
  const [showNotificationLogs, setShowNotificationLogs] = useState(false);

  // Modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({});
  const [emailModalData, setEmailModalData] = useState({});

  // Data
  const { data: users = [], isLoading: isLoadingUsers } = useQuery('allUsers', () =>
    usersAPI.getUsers()
  );
  const { data: adminUsers = [] } = useQuery('adminUsers', () => authAPI.getAdminUsers());
  const { data: groups = [] } = useQuery('allGroups', () => trainingGroupsAPI.getAllGroupsAdmin());
  const { data: crews = [] } = useQuery('allCrews', () => crewsAPI.getAllCrewsAdmin());
  const { data: pendingRequests = [], isLoading: isLoadingPendingRequests } = useQuery(
    'pendingRequests',
    () => authAPI.getPendingRequests(),
    { staleTime: 30 * 1000 }
  );

  // Statistics queries (always enabled)
  const { data: statisticsTrainingSessions = [] } = useQuery(
    'statisticsTrainingSessions',
    () => trainingSessionsAPI.getAllSessionsAdmin(),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );
  const { data: statisticsAutoplannerTasks = [] } = useQuery(
    'statisticsAutoplannerTasks',
    () => autoplannerAPI.getAllTasksAdmin(),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );
  const { data: statisticsNotificationLogs = [] } = useQuery(
    'statisticsNotificationLogs',
    () => notificationsAPI.getNotificationLogs(),
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );

  // Bulk actions queries (only enabled when bulk actions are shown)
  const { data: trainingSessions = [] } = useQuery(
    'allTrainingSessions',
    () => trainingSessionsAPI.getAllSessionsAdmin(),
    { enabled: showBulkActions }
  );
  const { data: autoplannerTasks = [] } = useQuery(
    'allAutoplannerTasks',
    () => autoplannerAPI.getAllTasksAdmin(),
    { enabled: showBulkActions }
  );
  const { data: notificationLogs = [], isLoading: isLoadingNotificationLogs } = useQuery(
    'notificationLogs',
    () => notificationsAPI.getNotificationLogs(),
    {
      enabled: showNotificationLogs,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const { data: userDetails, isLoading: isLoadingUserDetails } = useQuery(
    ['userDetails', detailsUserId],
    () => usersAPI.getUser(detailsUserId),
    { enabled: !!detailsUserId }
  );

  // Filter training groups to only show groups where the user is a coach
  const coachingTrainingGroupIds = getCoachingTrainingGroups(user);
  const coachingTrainingGroups = groups.filter((group) =>
    coachingTrainingGroupIds.includes(group.id)
  );

  // Mutations: user
  const updateUserMutation = useMutation(({ id, data }) => usersAPI.updateUser(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('allUsers');
      showSuccess('User updated');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const deleteUserMutation = useMutation((id) => usersAPI.deleteUser(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('allUsers');
      queryClient.invalidateQueries('adminUsers');
      showSuccess('User deleted');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const promoteToAdminMutation = useMutation((userId) => authAPI.promoteToAdmin(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('allUsers');
      showSuccess('Promoted to admin');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const demoteFromAdminMutation = useMutation((userId) => authAPI.demoteFromAdmin(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('allUsers');
      showSuccess('Demoted from admin');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const approveUserMutation = useMutation((userId) => authAPI.approveUser(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries('pendingRequests');
      queryClient.invalidateQueries('allUsers');
      showSuccess('User approved successfully');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const rejectUserMutation = useMutation((userId) => authAPI.rejectUser(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries('pendingRequests');
      queryClient.invalidateQueries('allUsers');
      showSuccess('User rejected successfully');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  if (!isAdmin(user)) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
        <p className="text-gray-600">You do not have access to this page.</p>
      </div>
    );
  }

  // Mutations: groups/crews (create/delete only)
  const createGroupMutation = useMutation((data) => trainingGroupsAPI.createGroup(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('allGroups');
      showSuccess('Training group created');
      setShowCreateGroupModal(false);
      setGroupForm({
        name: '',
        description: '',
        group_type: 'intermediate',
        is_active: true,
        availability_reminder_enabled: false,
        availability_reminder_day: 6,
      });
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });
  const deleteGroupMutation = useMutation((id) => trainingGroupsAPI.deleteGroup(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('allGroups');
      showSuccess('Training group deleted');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const updateGroupMutation = useMutation(
    ({ id, data }) => trainingGroupsAPI.updateGroup(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allGroups');
        showSuccess('Training group updated');
        setShowCreateGroupModal(false);
        setEditingGroup(null);
        setGroupForm({
          name: '',
          description: '',
          group_type: 'intermediate',
          is_active: true,
          availability_reminder_enabled: false,
          availability_reminder_day: 6,
        });
      },
      onError: (error) => showError(error.response?.data?.detail || error.message),
    }
  );

  const createCrewMutation = useMutation((data) => crewsAPI.createCrew(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('allCrews');
      showSuccess('Crew created');
      setIsCreateCrewOpen(false);
      setNewCrew({ name: '', boat_type: '4x', is_experimental: false });
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });
  const deleteCrewMutation = useMutation((id) => crewsAPI.deleteCrew(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('allCrews');
      showSuccess('Crew deleted');
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  const updateCrewMutation = useMutation(({ id, data }) => crewsAPI.updateCrew(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('allCrews');
      showSuccess('Crew updated');
      setIsEditCrewOpen(false);
      setEditingCrew(null);
    },
    onError: (error) => showError(error.response?.data?.detail || error.message),
  });

  // Bulk action mutations
  const bulkDeleteSessionsMutation = useMutation(
    (sessionIds) => trainingSessionsAPI.bulkDeleteSessions(sessionIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allTrainingSessions');
        showSuccess('Sessions deleted successfully');
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to delete sessions'));
      },
    }
  );

  const bulkAcceptUsersMutation = useMutation((userIds) => authAPI.bulkApproveUsers(userIds), {
    onSuccess: () => {
      queryClient.invalidateQueries('pendingRequests');
      queryClient.invalidateQueries('allUsers');
      showSuccess('Users approved successfully');
    },
    onError: (error) => {
      showError(extractErrorMessage(error, 'Failed to approve users'));
    },
  });

  const bulkDeleteTasksMutation = useMutation(
    (taskIds) => autoplannerAPI.bulkDeleteTasks(taskIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allAutoplannerTasks');
        showSuccess('Tasks deleted successfully');
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to delete tasks'));
      },
    }
  );

  const bulkDeleteCrewsMutation = useMutation((crewIds) => crewsAPI.bulkDeleteCrews(crewIds), {
    onSuccess: () => {
      queryClient.invalidateQueries('allCrews');
      showSuccess('Crews deleted successfully');
    },
    onError: (error) => {
      showError(extractErrorMessage(error, 'Failed to delete crews'));
    },
  });

  const bulkDeleteUsersMutation = useMutation((userIds) => usersAPI.bulkDeleteUsers(userIds), {
    onSuccess: (data) => {
      queryClient.invalidateQueries('allUsers');
      queryClient.invalidateQueries('adminUsers');
      queryClient.invalidateQueries('pendingRequests');
      showSuccess(data?.message || 'Users deleted successfully');
    },
    onError: (error) => {
      showError(extractErrorMessage(error, 'Failed to delete users'));
    },
  });

  const bulkRemoveUsersFromGroupMutation = useMutation(
    ({ groupId, userIds }) => trainingGroupsAPI.bulkRemoveMembers(groupId, userIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allGroups');
        showSuccess('Users removed from group successfully');
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to remove users from group'));
      },
    }
  );

  // Notification mutations
  const triggerAvailabilityRemindersMutation = useMutation(
    (force = false) => notificationsAPI.triggerAvailabilityReminders(force),
    {
      onSuccess: (data) => {
        showSuccess(
          `Availability reminders triggered: ${data.message}`
        );
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to trigger availability reminders'));
      },
    }
  );

  const triggerScheduleRemindersMutation = useMutation(
    () => notificationsAPI.triggerScheduleReminders(),
    {
      onSuccess: (data) => {
        showSuccess(`Schedule reminders triggered: ${data.total_sent} notifications sent`);
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to trigger schedule reminders'));
      },
    }
  );

  const triggerTestNotificationMutation = useMutation(
    () => notificationsAPI.sendTestNotification(1), // Send to first user for testing
    {
      onSuccess: (data) => {
        showSuccess(`Test notification sent: ${data.message}`);
      },
      onError: (error) => {
        showError(extractErrorMessage(error, 'Failed to send test notification'));
      },
    }
  );

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(term) ||
        (u.first_name || '').toLowerCase().includes(term) ||
        (u.last_name || '').toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const sortedUsers = useMemo(() => {
    const arr = [...filteredUsers];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortKey === 'last_login') {
        const ta = a.last_login ? new Date(a.last_login).getTime() : null;
        const tb = b.last_login ? new Date(b.last_login).getTime() : null;
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        if (ta < tb) return -1 * dir;
        if (ta > tb) return 1 * dir;
        return 0;
      }
      const nameA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
      const nameB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
      const roleA = a.is_admin ? 'admin' : 'rower';
      const roleB = b.is_admin ? 'admin' : 'rower';
      let va;
      let vb;
      switch (sortKey) {
        case 'id':
          va = a.id || 0;
          vb = b.id || 0;
          break;
        case 'name':
          va = nameA;
          vb = nameB;
          break;
        case 'email':
          va = (a.email || '').toLowerCase();
          vb = (b.email || '').toLowerCase();
          break;
        case 'role':
          va = roleA;
          vb = roleB;
          break;
        case 'active':
          va = a.is_active ? 1 : 0;
          vb = b.is_active ? 1 : 0;
          break;
        default:
          va = 0;
          vb = 0;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filteredUsers, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (isLoadingUsers) return <LoadingSpinner />;

  // Helpers
  const toggleUserActive = async (u) => {
    await updateUserMutation.mutateAsync({ id: u.id, data: { is_active: !u.is_active } });
  };

  const changeUserEmail = async (u) => {
    setEmailModalData({
      user: u,
      currentEmail: u.email,
      newEmail: u.email,
    });
    setShowEmailModal(true);
  };

  const resetUserPassword = async (u) => {
    // Leverage existing forgot-password flow
    await authAPI.requestPasswordReset(u.email);
    showSuccess('Password reset email sent');
  };

  const removeUser = async (u) => {
    setConfirmModalData({
      title: 'Confirm Delete',
      message: `Delete user ${u.first_name} ${u.last_name} (${u.email})? This cannot be undone.`,
      confirmText: 'Delete',
      confirmAction: () => deleteUserMutation.mutateAsync(u.id),
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const promoteUser = async (u) => {
    await promoteToAdminMutation.mutateAsync(u.id);
  };

  const demoteUser = async (u) => {
    setConfirmModalData({
      title: 'Confirm Admin Demotion',
      message: `Demote ${u.first_name} ${u.last_name} from admin?`,
      confirmText: 'Demote',
      confirmAction: () => demoteFromAdminMutation.mutateAsync(u.id),
      type: 'warning',
    });
    setShowConfirmModal(true);
  };

  const handleApproveUser = async (userId, userName) => {
    setConfirmModalData({
      title: 'Confirm Approval',
      message: `Approve ${userName}'s registration request?`,
      confirmText: 'Approve',
      confirmAction: () => approveUserMutation.mutateAsync(userId),
      type: 'success',
    });
    setShowConfirmModal(true);
  };

  const handleRejectUser = async (userId, userName) => {
    setConfirmModalData({
      title: 'Confirm Rejection',
      message: `Reject ${userName}'s registration request? This will permanently delete their account.`,
      confirmText: 'Reject',
      confirmAction: () => rejectUserMutation.mutateAsync(userId),
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleGroupFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;
    if (name === 'availability_reminder_day') processedValue = parseInt(value, 10);
    setGroupForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : processedValue }));
  };

  const handleCreateGroupSubmit = async (e) => {
    e.preventDefault();
    if (editingGroup) {
      await updateGroupMutation.mutateAsync({ id: editingGroup.id, data: groupForm });
    } else {
      await createGroupMutation.mutateAsync(groupForm);
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      group_type: group.group_type,
      availability_reminder_enabled: group.availability_reminder_enabled || false,
      availability_reminder_day: group.availability_reminder_day || 6,
    });
    setShowCreateGroupModal(true);
  };

  const deleteGroup = async (group) => {
    setConfirmModalData({
      title: 'Confirm Delete',
      message: `Delete training group "${group.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmAction: () => deleteGroupMutation.mutateAsync(group.id),
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleCreateCrew = () => setIsCreateCrewOpen(true);

  const handleCreateCrewSubmit = async (e) => {
    e.preventDefault();
    await createCrewMutation.mutateAsync(newCrew);
  };

  const handleUpdateCrewSubmit = async (e) => {
    e.preventDefault();
    await updateCrewMutation.mutateAsync({ id: editingCrew.id, data: editingCrew });
  };

  const handleEditCrew = (crew) => {
    setEditingCrew({
      ...crew,
      originalIsExperimental: crew.is_experimental, // Track original status
    });
    setIsEditCrewOpen(true);
  };

  const deleteCrew = async (crew) => {
    setConfirmModalData({
      title: 'Confirm Delete',
      message: `Delete crew "${crew.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmAction: () => deleteCrewMutation.mutateAsync(crew.id),
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  // Bulk action helpers
  const handleSelectAll = (type, items) => {
    if (type === 'users') {
      setSelectedUsers(new Set(items.map((item) => item.id)));
    } else if (type === 'bulk-delete-users') {
      setSelectedBulkDeleteUsers(new Set(items.map((item) => item.id)));
    } else if (type === 'groups') {
      setSelectedGroups(new Set(items.map((item) => item.id)));
    } else if (type === 'crews') {
      setSelectedCrews(new Set(items.map((item) => item.id)));
    }
  };

  const handleSelectItem = (type, id) => {
    if (type === 'users') {
      const newSelected = new Set(selectedUsers);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedUsers(newSelected);
    } else if (type === 'bulk-delete-users') {
      const newSelected = new Set(selectedBulkDeleteUsers);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedBulkDeleteUsers(newSelected);
    } else if (type === 'groups') {
      const newSelected = new Set(selectedGroups);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedGroups(newSelected);
    } else if (type === 'crews') {
      const newSelected = new Set(selectedCrews);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedCrews(newSelected);
    }
  };

  const handleBulkDeleteSessions = async () => {
    const sessionsToDelete = trainingSessions.filter((session) => {
      // Check if any of the session's crew assignments have crews that belong to selected training groups
      return session.crew_assignments?.some(
        (crewAssignment) =>
          crewAssignment.crew?.training_group_id &&
          selectedGroups.has(crewAssignment.crew.training_group_id)
      );
    });
    if (sessionsToDelete.length === 0) {
      showError('No sessions found for selected training groups');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Delete',
      message: `Delete ${sessionsToDelete.length} sessions from selected training groups? This cannot be undone.`,
      confirmText: 'Delete Sessions',
      confirmAction: async () => {
        await bulkDeleteSessionsMutation.mutateAsync(sessionsToDelete.map((s) => s.id));
        setSelectedGroups(new Set());
      },
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleBulkAcceptUsers = async () => {
    const usersToAccept = pendingRequests.filter((request) => selectedUsers.has(request.id));
    if (usersToAccept.length === 0) {
      showError('No pending users selected');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Approval',
      message: `Approve ${usersToAccept.length} pending users?`,
      confirmText: 'Approve Users',
      confirmAction: async () => {
        await bulkAcceptUsersMutation.mutateAsync(usersToAccept.map((u) => u.id));
        setSelectedUsers(new Set());
      },
      type: 'success',
    });
    setShowConfirmModal(true);
  };

  const handleBulkDeleteTasks = async () => {
    const tasksToDelete = autoplannerTasks.filter(
      (task) => task.training_group?.id && selectedGroups.has(task.training_group.id)
    );
    if (tasksToDelete.length === 0) {
      showError('No tasks found for selected training groups');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Delete',
      message: `Delete ${tasksToDelete.length} autoplanner tasks from selected training groups? This cannot be undone.`,
      confirmText: 'Delete Tasks',
      confirmAction: async () => {
        await bulkDeleteTasksMutation.mutateAsync(tasksToDelete.map((t) => t.id));
        setSelectedGroups(new Set());
      },
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleBulkDeleteCrews = async () => {
    const crewsToDelete = crews.filter((crew) => selectedCrews.has(crew.id));
    if (crewsToDelete.length === 0) {
      showError('No crews selected');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Delete',
      message: `Delete ${crewsToDelete.length} crews? This cannot be undone.`,
      confirmText: 'Delete Crews',
      confirmAction: async () => {
        await bulkDeleteCrewsMutation.mutateAsync(crewsToDelete.map((c) => c.id));
        setSelectedCrews(new Set());
      },
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleBulkDeleteUsers = async () => {
    const ids = [...selectedBulkDeleteUsers];
    if (ids.length === 0) {
      showError('No users selected');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Delete',
      message: `Permanently delete ${ids.length} user account(s)? This cannot be undone.`,
      confirmText: 'Delete Users',
      confirmAction: async () => {
        await bulkDeleteUsersMutation.mutateAsync(ids);
        setSelectedBulkDeleteUsers(new Set());
      },
      type: 'danger',
    });
    setShowConfirmModal(true);
  };

  const handleBulkRemoveUsersFromGroup = async (groupId) => {
    const usersToRemove = users.filter(
      (user) => selectedUsers.has(user.id) && user.training_groups?.some((tg) => tg.id === groupId)
    );
    if (usersToRemove.length === 0) {
      showError('No users selected for removal from this group');
      return;
    }
    setConfirmModalData({
      title: 'Confirm Bulk Removal',
      message: `Remove ${usersToRemove.length} users from this training group?`,
      confirmText: 'Remove Users',
      confirmAction: async () => {
        await bulkRemoveUsersFromGroupMutation.mutateAsync({
          groupId,
          userIds: usersToRemove.map((u) => u.id),
        });
        setSelectedUsers(new Set());
      },
      type: 'warning',
    });
    setShowConfirmModal(true);
  };

  // Notification handlers
  const handleGlobalAnnouncementSubmit = (e) => {
    e.preventDefault();
    if (!globalAnnouncement.title.trim() || !globalAnnouncement.message.trim()) {
      showError('Please fill in both title and message');
      return;
    }
    sendGlobalAnnouncementMutation.mutate(globalAnnouncement);
  };

  // Helper function to visualize rigging configuration
  const visualizeRiggingConfig = (riggingConfig) => {
    if (!riggingConfig) return null;

    const parsed = parseRiggingConfig(riggingConfig);
    if (!parsed || Object.keys(parsed).length === 0) return null;

    const seatEntries = Object.entries(parsed)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([seat, side]) => ({ seat: parseInt(seat), side }));

    return (
      <div className="flex flex-wrap gap-1">
        {seatEntries.map(({ seat, side }) => (
          <span
            key={seat}
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
              side === 'port'
                ? 'bg-red-100 text-red-800'
                : side === 'starboard'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
            }`}
            title={`Seat ${seat}: ${side} side`}
          >
            {seat}: {side}
          </span>
        ))}
      </div>
    );
  };

  // Helper function to scroll to bulk actions section for bulk remove
  const scrollToBulkActionsSection = () => {
    setTimeout(() => {
      const bulkActionsElement = document.getElementById('bulk-actions');
      if (bulkActionsElement) {
        bulkActionsElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100); // Small delay to ensure the element is rendered
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header and Statistics */}
      <div className="border-b border-gray-200 p-4 sm:p-6">
        <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">Admin Dashboard</h1>
        <p className="text-sm text-gray-600 sm:text-base">
          Manage users, system administrators, training groups and crews.
        </p>

        {/* Statistics Cards */}
        <div id="statistics">
          <StatisticsCards
            users={users}
            adminUsers={adminUsers}
            groups={groups}
            crews={crews}
            trainingSessions={statisticsTrainingSessions}
            autoplannerTasks={statisticsAutoplannerTasks}
            notificationLogs={statisticsNotificationLogs}
          />
        </div>
      </div>

      {/* Sticky Navigation */}
      <AdminNavigation />

      {/* Main Content */}
      <div className="space-y-6 p-4 sm:space-y-8 sm:p-6">
        {/* Bulk Actions */}
        <div id="bulk-actions">
          <BulkActions
            showBulkActions={showBulkActions}
            setShowBulkActions={setShowBulkActions}
            selectedUsers={selectedUsers}
            selectedBulkDeleteUsers={selectedBulkDeleteUsers}
            selectedGroups={selectedGroups}
            selectedCrews={selectedCrews}
            groups={groups}
            crews={crews}
            pendingRequests={pendingRequests}
            trainingSessions={trainingSessions}
            autoplannerTasks={autoplannerTasks}
            users={users}
            currentUserId={user?.id}
            handleSelectAll={handleSelectAll}
            handleSelectItem={handleSelectItem}
            handleBulkDeleteSessions={handleBulkDeleteSessions}
            handleBulkAcceptUsers={handleBulkAcceptUsers}
            handleBulkDeleteUsers={handleBulkDeleteUsers}
            handleBulkDeleteTasks={handleBulkDeleteTasks}
            handleBulkDeleteCrews={handleBulkDeleteCrews}
            handleBulkRemoveUsersFromGroup={handleBulkRemoveUsersFromGroup}
            bulkDeleteSessionsMutation={bulkDeleteSessionsMutation}
            bulkAcceptUsersMutation={bulkAcceptUsersMutation}
            bulkDeleteUsersMutation={bulkDeleteUsersMutation}
            bulkDeleteTasksMutation={bulkDeleteTasksMutation}
            bulkDeleteCrewsMutation={bulkDeleteCrewsMutation}
            bulkActionType={bulkActionType}
          />
        </div>

        {/* Pending Requests */}
        <div id="pending-requests">
          <PendingRequests
            pendingRequests={pendingRequests}
            approveUser={handleApproveUser}
            rejectUser={handleRejectUser}
          />
        </div>

        {/* Users Table */}
        <div id="users">
          <UsersTable
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortedUsers={sortedUsers}
            sortKey={sortKey}
            sortDir={sortDir}
            handleSort={handleSort}
            setDetailsUserId={setDetailsUserId}
            changeUserEmail={changeUserEmail}
            resetUserPassword={resetUserPassword}
            removeUser={removeUser}
            user={user}
            updateUserMutation={updateUserMutation}
          />
        </div>

        {/* System Administrators */}
        <div id="system-administrators">
          <SystemAdministrators
            adminUsers={adminUsers}
            users={users}
            user={user}
            promoteUser={promoteUser}
            demoteUser={demoteUser}
          />
        </div>

        {/* Training Groups */}
        <div id="training-groups">
          <TrainingGroups
            groups={groups}
            setShowCreateGroupModal={setShowCreateGroupModal}
            deleteGroup={deleteGroup}
            setSelectedUsers={setSelectedUsers}
            setBulkActionType={setBulkActionType}
            setShowBulkActions={setShowBulkActions}
            onEditGroup={handleEditGroup}
            scrollToBulkActionsSection={scrollToBulkActionsSection}
          />
        </div>

        {/* Crews */}
        <div id="crews">
          <Crews
            crews={crews}
            handleCreateCrew={handleCreateCrew}
            deleteCrew={deleteCrew}
            visualizeRiggingConfig={visualizeRiggingConfig}
            onEditCrew={handleEditCrew}
          />
        </div>

        {/* Admin Notifications */}
        <div id="notifications">
          <AdminNotifications
            showNotificationLogs={showNotificationLogs}
            setShowNotificationLogs={setShowNotificationLogs}
            notificationLogs={notificationLogs}
            isLoadingNotificationLogs={isLoadingNotificationLogs}
            queryClient={queryClient}
            triggerAvailabilityRemindersMutation={triggerAvailabilityRemindersMutation}
            triggerScheduleRemindersMutation={triggerScheduleRemindersMutation}
            triggerTestNotificationMutation={triggerTestNotificationMutation}
            trainingGroups={groups}
          />
        </div>
      </div>

      {/* Create Group Modal */}
      <CreateGroupModal
        show={showCreateGroupModal}
        onClose={() => {
          setShowCreateGroupModal(false);
          setEditingGroup(null);
          setGroupForm({
            name: '',
            description: '',
            group_type: 'intermediate',
            is_active: true,
            availability_reminder_enabled: false,
            availability_reminder_day: 6,
          });
        }}
        groupForm={groupForm}
        onGroupFormChange={handleGroupFormChange}
        onSubmit={handleCreateGroupSubmit}
        editingGroup={editingGroup}
        groupTypes={[
          'novice',
          'intermediate',
          'advanced',
          'elite',
          'masters',
          'junior',
          'development',
        ]}
        groupTypeLabels={{
          novice: 'Novice',
          intermediate: 'Intermediate',
          advanced: 'Advanced',
          elite: 'Elite',
          masters: 'Masters',
          junior: 'Junior',
          development: 'Development',
        }}
        isLoading={createGroupMutation.isLoading || updateGroupMutation.isLoading}
      />

      {/* Create Crew Modal */}
      <CreateCrewModal
        isOpen={isCreateCrewOpen}
        newCrew={newCrew}
        trainingGroups={coachingTrainingGroups}
        onClose={() => {
          setIsCreateCrewOpen(false);
          setNewCrew({ name: '', boat_type: '4x', is_experimental: false });
        }}
        onCrewChange={setNewCrew}
        onSubmit={handleCreateCrewSubmit}
      />

      {/* Edit Crew Modal */}
      <EditCrewModal
        isOpen={isEditCrewOpen}
        crew={editingCrew}
        trainingGroups={coachingTrainingGroups}
        onClose={() => {
          setIsEditCrewOpen(false);
          setEditingCrew(null);
        }}
        onCrewChange={setEditingCrew}
        onSubmit={handleUpdateCrewSubmit}
      />

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        user={emailModalData.user}
        onSubmit={async (userId, newEmail) => {
          await updateUserMutation.mutateAsync({ id: userId, data: { email: newEmail } });
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmModalData.confirmAction}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText={confirmModalData.confirmText}
        isDestructive={confirmModalData.type === 'danger'}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={!!detailsUserId}
        onClose={() => setDetailsUserId(null)}
        user={detailsUserId}
        users={users}
      />
    </div>
  );
};

export default Admin;
